import type { Page } from './App';
import type { PortfolioFilters } from './pages/PortfolioPage';

/** What the app saves per user (app_user_pref.workspace) and restores after
 * login: open participant tabs (ids + measure only — names re-resolve from
 * each drilldown's own fetch), the portfolio filter/sort state, and the page
 * the session left off on. */
export interface WorkspaceSnapshot {
  tabs: Array<{ id: number; measure?: number }>;
  portfolio: PortfolioFilters;
  page: Page;
  activeParticipantId: number | null;
}

const SORT_KEYS: PortfolioFilters['sortKey'][] = [
  'name', 'programme', 'measures', 'months', 'days', 'hours',
  'taux_activite', 'taux_cible', 'presence', 'journal',
  'mandant', 'rm', 'rp',
];
const PAGES: Page[] = ['portfolio', 'participant', 'admin', 'profile', 'equipe', 'conseillers', 'notifications', 'communications'];

const DEFAULT_FILTERS: PortfolioFilters = {
  filter: '', typeFilter: '', mandantFilter: '', rmFilter: '', rpFilter: '',
  sortKey: 'name', sortDir: 'asc',
};

/** Tolerant parse of a stored snapshot (the server already sanitizes on
 * write; this guards against hand-edited rows and shape drift). Returns null
 * when `raw` carries nothing usable — callers then keep defaults. */
export function parseWorkspace(raw: unknown): WorkspaceSnapshot | null {
  if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const w = raw as Record<string, unknown>;

  const tabs = (Array.isArray(w.tabs) ? w.tabs : [])
    .map((t): { id: number; measure?: number } | null => {
      if (t == null || typeof t !== 'object') return null;
      const id = Number((t as Record<string, unknown>).id);
      if (!Number.isInteger(id) || id <= 0) return null;
      const m = Number((t as Record<string, unknown>).measure);
      return { id, ...(Number.isInteger(m) && m > 0 ? { measure: m } : {}) };
    })
    .filter((t): t is { id: number; measure?: number } => t !== null)
    .slice(0, 12);

  const p = (w.portfolio ?? {}) as Record<string, unknown>;
  const str = (v: unknown): string => (typeof v === 'string' ? v.slice(0, 120) : '');
  const portfolio: PortfolioFilters = {
    ...DEFAULT_FILTERS,
    ...(str(p.filter) ? { filter: str(p.filter) } : {}),
    ...(str(p.typeFilter) ? { typeFilter: str(p.typeFilter) } : {}),
    ...(str(p.mandantFilter) ? { mandantFilter: str(p.mandantFilter) } : {}),
    ...(str(p.rmFilter) ? { rmFilter: str(p.rmFilter) } : {}),
    ...(str(p.rpFilter) ? { rpFilter: str(p.rpFilter) } : {}),
    ...(SORT_KEYS.includes(p.sortKey as PortfolioFilters['sortKey']) ? { sortKey: p.sortKey as PortfolioFilters['sortKey'] } : {}),
    ...(p.sortDir === 'desc' ? { sortDir: 'desc' as const } : {}),
  };

  const activeId = Number(w.activeParticipantId);
  const activeParticipantId = Number.isInteger(activeId) && activeId > 0 ? activeId : null;

  // A saved participant page is only restorable while its tab survived the
  // parse; otherwise fall back to the portfolio.
  const rawPage = w.page as Page;
  const page: Page = rawPage === 'participant'
    ? (activeParticipantId != null && tabs.some(t => t.id === activeParticipantId) ? 'participant' : 'portfolio')
    : (PAGES.includes(rawPage) ? rawPage : 'portfolio');

  return { tabs, portfolio, page, activeParticipantId: page === 'participant' ? activeParticipantId : null };
}
